/* eslint-disable camelcase */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import Lottie from 'react-lottie'
import {css} from '@emotion/core'
import {createMarkup, convertDatoCmsImgPathsToLocal} from '../graphql/datoCmsDataFactory'
import {getTheme, getThemeStyles} from '../util/theme'
import {getLanguageCode} from '../util/locale'

import Layout from '../components/Layout'
import Hero from '../components/Hero'
import AvInfo from '../components/AvInfo'

import anonymousViewData from '../translations/anonymous-view.json'

const AnonymousView = ({languageCode}) => {
    const lang = languageCode || getLanguageCode()

    const {
        anonymousSeo,
        anonymousSection1Description,
        anonymousSection1Image,
        anonymousSection1ImageDark,
        anonymousSection1Title,
        anonymousSection1Subtitle,
        anonymousSection2Title,
        anonymousSection2InfoBullets,
        anonymousSection3Description,
        anonymousSection3Title,
        anonymousSection4Bullets,
        anonymousSection4Image,
        anonymousSection4ImageDark,
        anonymousSection4Title,
        anonymousSection5Description,
        anonymousSection5Title,
        anonymousSection6Image,
        anonymousSection6ImageDark,
        anonymousSection6Title,
    } = anonymousViewData[lang]

    const section1Image = {
        light: anonymousSection1Image,
        air: anonymousSection1Image,
        dark: anonymousSection1ImageDark,
        night: anonymousSection1ImageDark,
    }

    const section4Image = {
        light: anonymousSection4Image,
        air: anonymousSection4Image,
        dark: anonymousSection4ImageDark,
        night: anonymousSection4ImageDark,
    }

    const section6Image = {
        light: anonymousSection6Image,
        air: anonymousSection6Image,
        dark: anonymousSection6ImageDark,
        night: anonymousSection6ImageDark,
    }

    const [lottieOptions, setLottieOptions] = useState({
        loop: true,
        autoplay: true,
    })

    const [theme, setTheme] = useState('light')
    const [themeStyles, setThemeStyles] = useState({})

    useEffect(() => {
        const theme = getTheme()
        const themeStyles = getThemeStyles()
        setTheme(theme)
        setThemeStyles(themeStyles)
        if (lang === `de`) {
            import('../images/lottie/anonViewDe.json').then((data) => {
                const {default: animationDataDe} = data
                setLottieOptions((opts) => ({
                    ...opts,
                    animationData: animationDataDe,
                }))
            })
        } else {
            import('../images/lottie/anonViewEn.json').then((data) => {
                const {default: animationDataEn} = data
                setLottieOptions((opts) => ({
                    ...opts,
                    animationData: animationDataEn,
                }))
            })
        }
    }, [themeStyles, lang])

    return (
        <Layout
            title={anonymousSeo.title}
            description={anonymousSeo.description}
            lang={lang}
            canonicalPath="/anonymous-view/"
            themeStyles={themeStyles}
            theme={theme}
        >
            <Hero
                subTitle={anonymousSection1Subtitle}
                title={anonymousSection1Title}
                imageUrl={section1Image[theme].url}
                body={anonymousSection1Description}
                theme={theme}
                themeStyles={themeStyles}
            />
            <section
                css={css`
                    padding: 2rem 1rem;
                    font-family: 'Inter', sans-serif;
                    background-color: ${themeStyles.primaryBackgroundColor};
                `}
            >
                <h2
                    css={css`
                        font-weight: 600;
                        font-size: 24px;
                        line-height: 32px;
                        max-width: 700px;
                        margin: 0 auto;
                        margin-bottom: 32px;
                        text-align: center;
                        color: ${themeStyles.primaryTextColor};

                        @media (max-width: 600px) {
                            font-size: 20px;
                            line-height: 28px;
                            height: 100%;
                        }
                    `}
                >
                    {anonymousSection2Title}
                </h2>

                <div
                    css={css`
                        display: flex;
                        max-width: 920px;
                        min-height: 232px;
                        justify-content: space-between;
                        flex-wrap: wrap;
                        margin: 0 auto;

                        @media (max-width: 600px) {
                            flex-direction: column;
                            flex-wrap: nowrap;
                            height: 100%;
                        }
                    `}
                >
                    {anonymousSection2InfoBullets.map((bullet, index) => {
                        return (
                            <AvInfo
                                text={bullet.text}
                                imageUrl={bullet.image.url}
                                imageDarkUrl={bullet.imageDark.url}
                                theme={theme}
                                themeStyles={themeStyles}
                                key={index}
                            />
                        )
                    })}
                </div>
            </section>
            <section
                css={css`
                    display: flex;
                    padding: 4rem 2rem;
                    background: ${themeStyles.secondaryBackgroundColor};
                    font-family: 'Inter', sans-serif;
                    white-space: pre-wrap;
                    font-size: 16px;
                    line-height: 24px;
                `}
            >
                <div
                    css={css`
                        max-width: 1200px;
                        display: flex;
                        margin: 0 auto;

                        @media (max-width: 900px) {
                            flex-direction: column;
                        }
                    `}
                >
                    <div
                        css={css`
                            display: flex;
                            flex-direction: column;
                            margin-right: 50px;

                            @media (max-width: 900px) {
                                margin: 0;
                                margin-bottom: 32px;
                            }
                        `}
                    >
                        <h2
                            css={css`
                                max-width: 600px;
                                line-height: 46px;
                                font-size: 40px;
                                font-weight: 600;
                                margin-bottom: 24px;
                                color: ${themeStyles.primaryTextColor};
                            `}
                        >
                            {anonymousSection3Title}
                        </h2>
                        <div
                            css={css`
                                max-width: 900px;
                                margin: auto;
                                color: ${themeStyles.primaryTextColor};
                                margin: 0;
                            `}
                        >
                            {anonymousSection3Description}
                        </div>
                    </div>
                    <Lottie
                        css={css`
                            width: 100%;
                            max-width: 608px;
                            height: auto;
                            margin: auto;
                        `}
                        options={lottieOptions}
                    />
                </div>
            </section>
            <section
                css={css`
                    padding: 4rem 2rem 0rem 1rem;
                    font-family: 'Inter', sans-serif;
                    background: ${themeStyles.primaryBackgroundColor};

                    @media (max-width: 600px) {
                        padding: 4rem 2rem;
                    }
                `}
            >
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        max-width: 1200px;
                        margin: auto;
                        h2 {
                            font-size: 40px;
                            line-height: 46px;
                            font-family: 'Inter', sans-serif;
                            font-weight: 600;
                            margin: 0 0 32px;
                            text-align: center;
                            color: ${themeStyles.primaryTextColor};

                            @media (max-width: 600px) {
                                text-align: left;
                                font-size: 32px;
                                line-height: 40px;
                            }
                        }
                    `}
                >
                    <h2>{anonymousSection4Title}</h2>
                    <div
                        css={css`
                            display: flex;

                            @media (max-width: 900px) {
                                flex-direction: column-reverse;
                            }
                        `}
                    >
                        <img
                            css={css`
                                width: 470px;
                                max-width: 100%;
                                height: auto;
                                margin: auto;
                                padding-right: 70px;

                                @media (max-width: 900px) {
                                    padding-right: 50px;
                                }
                            `}
                            src={convertDatoCmsImgPathsToLocal(section4Image[theme].url)}
                            alt=""
                        />
                        <div
                            css={css`
                                max-width: 550px;
                                ul {
                                    padding: 0 2rem 0 1rem;
                                }
                                li {
                                    line-height: 24px;
                                    font-size: 16px;
                                    font-weight: 400;
                                    margin: 1rem 0;
                                    color: ${themeStyles.primaryTextColor};
                                }

                                @media (max-width: 900px) {
                                    max-width: 100%;
                                    margin-bottom: 56px;
                                }

                                @media (max-width: 600px) {
                                    ul {
                                        padding: 0 0 0 1rem;
                                    }
                                }
                            `}
                        >
                            <ul>
                                {anonymousSection4Bullets.map((bullet, index) => {
                                    return <li key={index}>{bullet.bulletText}</li>
                                })}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
            <section
                css={css`
                    display: flex;
                    flex-direction: column;
                    padding: 2rem 1rem 4rem 1rem;
                    line-height: 26px;
                    font-size: 16px;
                    text-align: center;
                    background: ${themeStyles.primaryBackgroundColor};

                    @media (max-width: 600px) {
                        display: none;
                        padding: 4rem 2rem;
                    }
                `}
            >
                <div
                    css={css`
                        font-weight: 500;
                        color: ${themeStyles.primaryTextColor};
                    `}
                >
                    {anonymousSection5Title}
                </div>
                <div
                    dangerouslySetInnerHTML={createMarkup(anonymousSection5Description)}
                    css={css`
                        p {
                            color: ${themeStyles.primaryTextColor};
                            a {
                                color: ${themeStyles.linkColor};
                            }
                        }
                    `}
                />
            </section>

            <section
                css={css`
                    display: flex;
                    flex-direction: column;
                    padding: 4rem 2rem;
                    margin: 0 auto;
                    text-align: center;
                    align-items: center;
                    background: ${themeStyles.secondaryBackgroundColor};
                `}
            >
                <img
                    css={css`
                        width: 142px;
                        heiht: 53px;
                        margin-bottom: 60px;
                    `}
                    src={convertDatoCmsImgPathsToLocal(section6Image[theme].url)}
                    alt=""
                />
                <h2
                    css={css`
                        font-weight: 600;
                        font-size: 30px;
                        line-height: 40px;
                        max-width: 850px;
                        color: ${themeStyles.primaryTextColor};
                    `}
                >
                    {anonymousSection6Title}
                </h2>
            </section>
        </Layout>
    )
}

AnonymousView.propTypes = {
    languageCode: PropTypes.string,
}

export default AnonymousView
